import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CriarPublicacaoDto,
  PlataformaDePublicacaoDto,
  PublicacaoDto,
  TipoDePublicacaoDto,
} from "@base/contracts";
import { planejarEdicaoDoGrupo } from "../domain/plano-edicao-grupo";
import { useAuth } from "../../../shared/auth/auth.contexto";
import {
  usePublicacoesGateway,
  usePublicacoesGatewayOpcional,
} from "./publicacoes.contexto";

export function useTemPublicacoes(): boolean {
  const { sessao } = useAuth();
  return Boolean(sessao?.escopos.includes("publicacoes:ler"));
}

export function useTemGerirPublicacoes(): boolean {
  const { sessao } = useAuth();
  return Boolean(sessao?.escopos.includes("publicacoes:gerir"));
}

export function useCalendarioPublicacoes(de?: string, ate?: string) {
  const gateway = usePublicacoesGateway();
  const { sessao } = useAuth();
  return useQuery({
    queryKey: ["publicacoes", "calendario", sessao?.tenantId, de, ate],
    queryFn: () => gateway.calendario(de, ate),
  });
}

export function useObterPublicacao(id: string | undefined) {
  const gateway = usePublicacoesGateway();
  const { sessao } = useAuth();
  return useQuery({
    queryKey: ["publicacoes", "obter", sessao?.tenantId, id],
    queryFn: () => gateway.obter(id!),
    enabled: Boolean(id),
  });
}

export function useFalhasPublicacoes() {
  const gateway = usePublicacoesGateway();
  const { sessao } = useAuth();
  return useQuery({
    queryKey: ["publicacoes", "falhas", sessao?.tenantId],
    queryFn: () => gateway.listar({ soFalhas: true }),
  });
}

export function usePublicadosPublicacoes() {
  const gateway = usePublicacoesGateway();
  const { sessao } = useAuth();
  return useQuery({
    queryKey: ["publicacoes", "publicados", sessao?.tenantId],
    queryFn: () => gateway.listar({ status: "publicado" }),
  });
}

export function useContagemFalhasPublicacoes(habilitado: boolean) {
  const gateway = usePublicacoesGatewayOpcional();
  const { sessao } = useAuth();
  return useQuery({
    queryKey: ["publicacoes", "falhas", "contagem", sessao?.tenantId],
    queryFn: () => {
      if (!gateway) return { total: 0 };
      return gateway.contagemFalhas();
    },
    enabled: habilitado && Boolean(gateway),
    refetchInterval: 60_000,
  });
}

export function useBuscarPerfilInstagram(username: string) {
  const gateway = usePublicacoesGateway();
  const limpo = username.trim().replace(/^@/, "");
  return useQuery({
    queryKey: ["publicacoes", "instagram", "perfil", limpo],
    queryFn: () => gateway.buscarPerfilInstagram(limpo),
    enabled: limpo.length >= 2,
    staleTime: 60_000,
  });
}

export function useCriarEAgendarPublicacao() {
  const gateway = usePublicacoesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entrada: {
      criar: CriarPublicacaoDto;
      arquivos?: File[];
      midiaIds?: readonly string[];
      agendarPara: string;
    }) => {
      const plataformas =
        entrada.criar.plataformas?.length
          ? [...new Set(entrada.criar.plataformas)]
          : [entrada.criar.plataforma ?? "instagram"];

      const resultados = [];
      for (const plataforma of plataformas) {
        const criada = await gateway.criar({
          nome: entrada.criar.nome,
          legenda: entrada.criar.legenda,
          tipo: entrada.criar.tipo,
          plataforma,
          // Collab e recurso do Instagram; nas outras redes o campo e ignorado.
          colaboradores:
            plataforma === "instagram"
              ? (entrada.criar.colaboradores ?? [])
              : [],
        });
        if (entrada.midiaIds && entrada.midiaIds.length > 0) {
          await gateway.anexarMidiasDaBiblioteca(criada.id, entrada.midiaIds);
        } else {
          await gateway.anexarMidias(criada.id, entrada.arquivos ?? []);
        }
        const aprovada = await gateway.aprovar(criada.id);
        resultados.push(
          await gateway.agendar(aprovada.id, {
            agendarPara: entrada.agendarPara,
          }),
        );
      }
      return resultados;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["publicacoes"] });
    },
  });
}

/** De onde vêm as mídias na edição. */
export type MidiasDaEdicao =
  | { readonly origem: "atuais" }
  | { readonly origem: "computador"; readonly arquivos: File[] }
  | { readonly origem: "biblioteca"; readonly midiaIds: readonly string[] };

/**
 * Edita um card da agenda inteiro (todas as redes de uma vez).
 * Ordem: conteúdo → mídia → data. A data por último porque é ela que põe o
 * post de volta na fila, já com a mídia nova.
 */
export function useEditarGrupoPublicacao() {
  const gateway = usePublicacoesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entrada: {
      readonly itens: readonly PublicacaoDto[];
      readonly nome: string;
      readonly legenda: string;
      readonly tipo: TipoDePublicacaoDto;
      readonly colaboradores: readonly string[];
      readonly plataformas: readonly PlataformaDePublicacaoDto[];
      readonly midias: MidiasDaEdicao;
      readonly agendarPara: string;
    }) => {
      const plano = planejarEdicaoDoGrupo(entrada.itens, entrada.plataformas);
      const origemDasMidias = entrada.itens[0];
      const collabDa = (plataforma: PlataformaDePublicacaoDto) =>
        plataforma === "instagram" ? [...entrada.colaboradores] : [];

      async function trocarMidias(id: string) {
        const { midias } = entrada;
        if (midias.origem === "computador") {
          await gateway.anexarMidias(id, midias.arquivos);
        } else if (midias.origem === "biblioteca") {
          await gateway.anexarMidiasDaBiblioteca(id, midias.midiaIds);
        } else if (origemDasMidias && origemDasMidias.id !== id) {
          await gateway.copiarMidias(id, origemDasMidias.id);
        }
      }

      for (const pub of plano.editar) {
        await gateway.editar(pub.id, {
          nome: entrada.nome,
          legenda: entrada.legenda,
          tipo: entrada.tipo,
          colaboradores: collabDa(pub.plataforma),
        });
        if (entrada.midias.origem !== "atuais") {
          await trocarMidias(pub.id);
        }
        await gateway.editar(pub.id, { agendarPara: entrada.agendarPara });
      }

      for (const plataforma of plano.criar) {
        const criada = await gateway.criar({
          nome: entrada.nome,
          legenda: entrada.legenda,
          tipo: entrada.tipo,
          plataforma,
          colaboradores: collabDa(plataforma),
        });
        await trocarMidias(criada.id);
        await gateway.aprovar(criada.id);
        await gateway.agendar(criada.id, { agendarPara: entrada.agendarPara });
      }

      // Por último: se algo acima falhar, nenhuma rede some à toa.
      for (const pub of plano.remover) {
        await gateway.excluir(pub.id);
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["publicacoes"] });
    },
  });
}

export function useReprocessarPublicacao() {
  const gateway = usePublicacoesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gateway.reprocessar(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["publicacoes"] });
    },
  });
}

export function useExcluirPublicacao() {
  const gateway = usePublicacoesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: readonly string[]) => {
      for (const id of ids) {
        await gateway.excluir(id);
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["publicacoes"] });
    },
  });
}
